import { useEffect, useState } from 'react';
import { DayNames } from '../../constants/calendar.constant';
import { CalendarMonthProperties } from '../../util/calendar-properties';
import { CalendarEvent } from './Calendar';

const MonthView = ({ events, today, currentMonth, currentYear }: PropsType) => {
  const [monthlyViewEvents, setMonthlyViewEvents] = useState<CalendarEvent[]>(
    []
  );
  const {
    daysInMonth,
    startingBlankDays,
    endingBlankDays,
    eventColor,
    isToday,
  } = CalendarMonthProperties(today, currentMonth, currentYear);

  const getEvents = (date: number) => {
    return monthlyViewEvents.filter(
      (e) =>
        new Date(e.start).toDateString() ===
        new Date(currentYear, currentMonth, date).toDateString()
    );
  };

  const convertToMonthlyView = (
    events: CalendarEvent[],
    currentMonth: number,
    currentYear: number
  ) => {
    const parsedEndOfMonth = new Date(
      currentMonth === 11 ? currentYear + 1 : currentYear,
      currentMonth === 11 ? 0 : currentMonth + 1,
      0,
      23,
      59,
      59,
      999
    );

    const monthlyViewEvents: CalendarEvent[] = [];

    events
      .filter((event) => {
        return (
          event.start.getMonth() <= currentMonth &&
          event.start.getFullYear() <= currentYear &&
          event.end.getMonth() >= currentMonth &&
          event.end.getFullYear() >= currentYear
        );
      })
      .forEach((event) => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);

        let currentEventStart = eventStart;

        while (
          currentEventStart < eventEnd &&
          currentEventStart <= parsedEndOfMonth
        ) {
          const currentEventEnd = new Date(currentEventStart);
          currentEventEnd.setHours(23, 59, 59, 999); // end of the day

          // Check if the event ends before the end of the day or before the end of the month
          if (
            eventEnd < currentEventEnd ||
            currentEventEnd > parsedEndOfMonth
          ) {
            currentEventEnd.setTime(eventEnd.getTime());
          }

          monthlyViewEvents.push({
            ...event,
            start: new Date(currentEventStart), // Store the full date-time for start
            end: new Date(currentEventEnd), // Store the full date-time for end
          });

          // Move to the next day, starting from the beginning of the day
          currentEventStart.setDate(currentEventStart.getDate() + 1);
          currentEventStart.setHours(0, 0, 0, 0);
        }
      });
    return monthlyViewEvents;
  };

  useEffect(() => {
    if (!events) return;
    setMonthlyViewEvents(
      convertToMonthlyView(events, currentMonth, currentYear)
    );
  }, [events, currentMonth, currentYear]);

  return (
    <div className="lg:flex lg:h-full lg:flex-col">
      <div className="shadow ring-1 ring-black ring-opacity-5 lg:flex lg:flex-auto lg:flex-col">
        <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none">
          {DayNames.map((day) => (
            <div key={day} className="bg-white py-2">
              {day.substring(0, 1)}
              <span className="sr-only sm:not-sr-only">
                {day.substring(1, 3)}
              </span>
            </div>
          ))}
        </div>
        <div className="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto">
          <div className=" w-full grid grid-cols-7 lg:gap-px">
            {startingBlankDays &&
              startingBlankDays.map((blankday) => {
                return (
                  <div
                    className="bg-slate-50 h-20 sm:h-28 lg:h-36"
                    key={blankday}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      height="100%"
                    >
                      <rect width="100%" height="100%" fill="url(#stripes)" />
                    </svg>
                  </div>
                );
              })}
            {daysInMonth.map((day) => {
              return (
                <div
                  className="relative bg-white h-20 sm:h-28 lg:h-36 overflow-hidden"
                  key={day}
                >
                  <div className="h-full flex flex-col justify-between">
                    {/* Events */}
                    <div className="grow flex flex-col relative p-0.5 sm:p-1.5 overflow-hidden">
                      {getEvents(day).map((event) => {
                        return (
                          <button
                            className="relative text-left mb-1"
                            key={event.title}
                          >
                            <div
                              className={`px-2 py-0.5 rounded overflow-hidden ${eventColor(
                                event.color ?? 'sky'
                              )}`}
                            >
                              {/* Event name */}
                              <div className="text-xs font-semibold truncate">
                                {event.title}
                              </div>
                              {/* Event time */}
                              <div className="text-xs uppercase truncate hidden sm:block">
                                {/* Start date */}
                                {event.start && (
                                  <span>
                                    {event.start.toLocaleTimeString([], {
                                      hour12: true,
                                      hour: 'numeric',
                                      minute: 'numeric',
                                    })}
                                  </span>
                                )}
                                {/* End date */}
                                {event.end && (
                                  <span>
                                    -{' '}
                                    <span>
                                      {event.end.toLocaleTimeString([], {
                                        hour12: true,
                                        hour: 'numeric',
                                        minute: 'numeric',
                                      })}
                                    </span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                      <div
                        className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent pointer-events-none"
                        aria-hidden="true"
                      ></div>
                    </div>
                    {/* Cell footer */}
                    <div className="flex justify-between items-center p-0.5 sm:p-1.5">
                      {/* More button (if more than 2 events) */}
                      {getEvents(day).length > 2 && (
                        <button className="text-xs text-slate-500  font-medium whitespace-nowrap text-center sm:py-0.5 px-0.5 sm:px-2 border border-slate-200  rounded">
                          <span className="md:hidden">+</span>
                          <span>{getEvents(day).length - 2}</span>{' '}
                          <span className="hidden md:inline">more</span>
                        </button>
                      )}
                      {/* Day number */}
                      <button
                        className={`inline-flex ml-auto w-6 h-6 items-center justify-center text-xs sm:text-sm  font-medium text-center rounded-full hover:bg-indigo-100 ${
                          isToday(day) &&
                          'flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white'
                        }`}
                      >
                        {day}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {endingBlankDays.map((blankday) => {
              return (
                <div
                  className="bg-slate-50 h-20 sm:h-28 lg:h-36"
                  key={blankday}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="100%"
                    height="100%"
                  >
                    <rect width="100%" height="100%" fill="url(#stripes)" />
                  </svg>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

type PropsType = {
  events: CalendarEvent[];
  today: Date;
  currentMonth: number;
  currentYear: number;
};

export default MonthView;
